import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';
import { ViewIcon, EditIcon, DeleteIcon, StatusIcon } from '../components/IconButtons';
import AdminSidebar from '../components/AdminSidebar';
import * as XLSX from 'xlsx';

const ManajemenGuruPage = () => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: true // Open by default since we're in a master data page
  });
  const [user, setUser] = useState(null);

  // Predefined class options - you can modify these based on your school's actual classes
  const [classes] = useState([
    'XIITP1',
    'XIITP2',
    'XIITP3',
    'XIITP4',
    'XIITP5',
    'XIITP6',
    'XITP2',
    'XITP3',
    'XITP4',
    'XITP5',
    'XITP6',
    'XMRPL',
    'XTP1',
    'XTP2',
    'XTP3',
    'XTP4',
    'XTP5',
    'XTP6',
    'XITP1'
  ]);

  // Real student data from API
  const [teachers, setTeachers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTeachers = async () => {
      setLoading(true);
      try { 
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        // Use absolute URL to avoid any issues with environment variables
        let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/teachers?page=${currentPage}`;
        if (searchQuery && searchQuery.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
        }
        
        const response = await authFetch(apiUrl);
        if (!response.ok) throw new Error('Failed to fetch teachers');
        const data = await response.json();
        setTeachers(data.data);
        setLastPage(data.last_page);
        setTotal(data.total);
      } catch (err) {
        setNotification({ show: true, message: 'Gagal memuat data guru: ' + err.message, type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchTeachers();
  }, [currentPage, searchQuery]);

  const [formData, setFormData] = useState({
    teacherId: '',
    name: '',
    email: '',
    headOfClass: '',
    teachingSubject: '',
    classType: [],
    avatarUrl: '',
    avatarFile: null
  });

  // No need to filter teachers on the frontend anymore, backend search is used.
  const filteredTeachers = teachers;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClassTypeChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      classType: checked
        ? [...prev.classType, value]
        : prev.classType.filter(type => type !== value)
    }));
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    // Use FormData for all fields including avatar
    const formDataToSend = new FormData();
    formDataToSend.append('teacherId', formData.teacherId);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('headOfClass', formData.headOfClass);
    formDataToSend.append('teachingSubject', formData.teachingSubject);
    // Append each class type separately for FormData
    if (Array.isArray(formData.classType)) {
      formData.classType.forEach((classType, index) => {
        formDataToSend.append(`class_type[${index}]`, classType);
      });
    }
    // If avatar file selected, append it
    if (formData.avatarFile) {
      formDataToSend.append('avatar', formData.avatarFile);
    }
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/teachers`;
     
      const response = await authFetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        console.error('API error:', errorData);
        
        // Handle validation errors specifically
        if (response.status === 422 && errorData.errors) {
          // Format validation errors nicely
          const errorMessages = [];
          for (const field in errorData.errors) {
            errorMessages.push(errorData.errors[field][0]);
          }
          setNotification({
            show: true,
            message: 'Validasi gagal: ' + errorMessages.join(', '),
            type: 'error'
          });
        } else {
          setNotification({
            show: true,
            message: 'Gagal menambah guru: ' + (errorData.message || JSON.stringify(errorData)),
            type: 'error'
          });
        }
        return;
      }

      const newTeacher = await response.json();

      // Show success notification
      setNotification({
        show: true,
        message: 'Guru berhasil ditambahkan!',
        type: 'success'
      });

      setTeachers(prev => [...prev, newTeacher]);
      setFormData({
        teacherId: '',
        name: '',
        email: '',
        headOfClass: '',
        teachingSubject: '',
        classType: [],
        avatarUrl: '',
        avatarFile: null       
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Network error:', err);
      setNotification({
        show: true,
        message: 'Terjadi kesalahan jaringan: ' + err.message,
        type: 'error'
      });
    }
  };

  const handleEditTeacher = async (e) => {
    e.preventDefault();
    if (!selectedTeacher) return;
    const formDataToSend = new FormData();
    formDataToSend.append('teacherId', formData.teacherId);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('headOfClass', formData.headOfClass);
    formDataToSend.append('teachingSubject', formData.teachingSubject);
    // Append each class type separately for FormData
    if (Array.isArray(formData.classType)) {
      formData.classType.forEach((classType, index) => {
        formDataToSend.append(`class_type[${index}]`, classType);
      });
    }
   
    if (formData.avatarFile) {
      formDataToSend.append('avatar', formData.avatarFile);
    }
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/teachers/${selectedTeacher.id}`;
      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'X-HTTP-Method-Override': 'PATCH'
        },
        body: formDataToSend
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try { errorData = JSON.parse(errorText); } catch (e) { errorData = { message: errorText }; }
        
        // Handle validation errors specifically
        if (response.status === 422 && errorData.errors) {
          // Format validation errors nicely
          const errorMessages = [];
          for (const field in errorData.errors) {
            errorMessages.push(errorData.errors[field][0]);
          }
          setNotification({
            show: true,
            message: 'Validasi gagal: ' + errorMessages.join(', '),
            type: 'error'
          });
        } else {
          setNotification({ show: true, message: 'Gagal mengedit guru: ' + (errorData.message || JSON.stringify(errorData)), type: 'error' });
        }
        return;
      }
      const updatedTeacher = await response.json();
      setNotification({ show: true, message: 'Guru berhasil diperbarui!', type: 'success' });
      setTeachers(prev => prev.map(teacher => teacher.id === selectedTeacher.id ? updatedTeacher : teacher));
      setShowEditModal(false);
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan: ' + err.message, type: 'error' });
    }
  };

  const handleDeleteTeacher = async () => {
    if (!selectedTeacher) return;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
    const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/teachers/${selectedTeacher.id}`;
    try {
      const response = await authFetch(apiUrl, { method: 'DELETE' });
      if (!response.ok) throw new Error('Gagal menghapus guru.');
      setTeachers(prev => prev.filter(teacher => teacher.id !== selectedTeacher.id));
      setNotification({ show: true, message: 'Guru berhasil dihapus!', type: 'success' });
    } catch (err) {
      setNotification({ show: true, message: err.message || 'Gagal menghapus guru.', type: 'error' });
    }
    setShowDeleteModal(false);
  };

  const openEditModal = (teacher) => {
    setSelectedTeacher(teacher);
    setFormData({
      teacherId: teacher.teacherId || '',
      name: teacher.name || '',
      email: teacher.email || '',
      headOfClass: teacher.headOfClass || '',
      teachingSubject: teacher.teachingSubject || '',
      classType: Array.isArray(teacher.class_type) ? teacher.class_type : (teacher.class_type ? [teacher.class_type] : []),
      avatarUrl: teacher.avatarUrl || '',
      avatarFile: null,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (teacher) => {
    setSelectedTeacher(teacher);
    setShowDeleteModal(true);
  };

  const handleWaliKelasStatus = (teacher) => {
    // Navigate to Admin Wali Kelas Monitoring page with class parameter
    navigate(`/admin/wali-kelas-monitoring?class=${encodeURIComponent(teacher.headOfClass)}`);
  };

  const handleFileUpload = (e) => {
    console.log('File upload triggered');
    const file = e.target.files[0];
    console.log('Selected file:', file);
    
    if (file) {
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size
      });
      
      setImportFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        console.log('FileReader onload triggered');
        try {
          console.log('Starting Excel processing...');
          const data = new Uint8Array(event.target.result);
          console.log('Data length:', data.length);
          
          const workbook = XLSX.read(data, { type: 'array' });
          console.log('Workbook loaded, sheets:', workbook.SheetNames);
          
          const sheetName = workbook.SheetNames[0];
          console.log('Using sheet:', sheetName);
          
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          console.log('JSON data extracted, rows:', jsonData.length);
          
          // Debug: Check what columns are actually in the Excel file
          if (jsonData.length > 0) {
            console.log('First row columns:', Object.keys(jsonData[0]));
            console.log('First row data:', jsonData[0]);
          }

          // Map Excel columns to our format
          const mappedData = jsonData.map((row, index) => {
            // Helper function to safely get string value
            const safeString = (value) => {
              if (value === null || value === undefined) return '';
              return String(value).trim();
            };

            const teacherIdRaw = row['ID Guru'] || row['id_guru'] || row['teacherId'] || row['Teacher ID'] || '';
            const nameRaw = row['Nama'] || row['nama'] || row['name'] || row['Name'] || '';
            const emailRaw = row['Email'] || row['email'] || row['E-mail'] || '';
            const headOfClassRaw = row['Wali Kelas'] || row['wali_kelas'] || row['headOfClass'] || row['Head Of Class'] || '';
            const teachingSubjectRaw = row['Mata Pelajaran'] || row['mata_pelajaran'] || row['teachingSubject'] || row['Subject'] || '';
            const classTypeRaw = row['Tipe Kelas'] || row['tipe_kelas'] || row['class_type'] || row['classType'] || row['Class Type'] || '';

            const teacherId = safeString(teacherIdRaw);
            const name = safeString(nameRaw);
            const email = safeString(emailRaw);
            const headOfClass = safeString(headOfClassRaw);
            const teachingSubject = safeString(teachingSubjectRaw);
            const classType = safeString(classTypeRaw)
              .split(',')
              .map(s => s.trim().toLowerCase())
              .filter(s => s && ['praktek', 'teori', 'bk'].includes(s));

            // Validation: Only teacherId and Name are required
            const validationErrors = [];
            if (teacherId === '') {
              validationErrors.push('ID Guru tidak boleh kosong');
            }
            if (name === '') {
              validationErrors.push('Nama tidak boleh kosong');
            }
            // Email is optional - no validation needed
            
            const isValid = validationErrors.length === 0;

            return {
              row: index + 2, // Excel row number (starting from 2, assuming row 1 is header)
              teacherId: teacherId,
              name: name,
              email: email,
              headOfClass: headOfClass,
              teachingSubject: teachingSubject,
              classType: classType,
              valid: isValid,
              errors: validationErrors
            };
          }).filter(item => {
            // Filter out completely empty rows
            return item.teacherId !== '' || item.name !== '' || item.email !== '';
          });

          console.log('Raw Excel data:', jsonData);
          console.log('Mapped data:', mappedData);
          console.log('Valid data count:', mappedData.filter(item => item.valid).length);
          
          setImportData(mappedData);
        } catch (error) {
          console.error('Excel parsing error:', error);
          setNotification({
            show: true,
            message: 'Error membaca file Excel: ' + error.message,
            type: 'error'
          });
        }
      };
      console.log('Starting FileReader...');
      reader.readAsArrayBuffer(file);
    } else {
      console.log('No file selected');
    }
  };

  const handleImportSubmit = async () => {
    console.log('Import submit called. Import data length:', importData.length);
    console.log('Import data:', importData);
    
    if (importData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data untuk diimpor',
        type: 'error'
      });
      return;
    }

    const validData = importData.filter(item => item.valid);
    console.log('Valid data count:', validData.length);
    console.log('Valid data:', validData);
    
    if (validData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data valid untuk diimpor',
        type: 'error'
      });
      return;
    }

    setImportLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/teachers/bulk-import`;
      console.log('API URL:', apiUrl);

      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          data: validData.map(item => ({
            teacherId: item.teacherId || '',
            name: item.name || '',
            email: item.email && item.email.trim() !== '' ? item.email.trim() : null,
            headOfClass: item.headOfClass || '',
            teachingSubject: item.teachingSubject || '',
            class_type: item.classType || []
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Import gagal');
      }

      const result = await response.json();
      
      // If there are errors, show sample errors for debugging
      let message = result.message;
      if (result.sample_errors && result.sample_errors.length > 0) {
        message += '\n\nSample errors:\n' + result.sample_errors.map(err => 
          `Row ${err.row}: ${err.error} (ID Guru: ${err.teacherId}, Name: ${err.name})`
        ).join('\n');
      }
      
      setNotification({
        show: true,
        message: message,
        type: result.summary?.errors > 0 ? 'error' : 'success'
      });

      // Refresh the teachers list
      const fetchTeachers = async () => {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
          let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/teachers?page=${currentPage}`;
          if (searchQuery && searchQuery.trim() !== '') {
            apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
          }
          const response = await authFetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            setTeachers(data.data);
            setLastPage(data.last_page);
            setTotal(data.total);
          }
        } catch (err) {
          console.error('Error refreshing data:', err);
        }
      };
      
      fetchTeachers();
      setShowImportModal(false);
      setImportFile(null);
      setImportData([]);

    } catch (error) {
      setNotification({
        show: true,
        message: 'Error mengimpor data: ' + error.message,
        type: 'error'
      });
    } finally {
      setImportLoading(false);
    }
  };

  // Hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  // Get user data for sidebar
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);
  
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : ''; 

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <h1>Manajemen Guru</h1>
          <p>Kelola data guru dengan mudah dan efisien</p>
        </div>
      </PageHeader>
      
      <DashboardGrid>
        <MainSection>
          <ActionHeader>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Cari guru berdasarkan nama, ID Guru, atau mata pelajaran"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
            <ActionButtonGroup>
              <ImportButton onClick={() => {
                setImportFile(null);
                setImportData([]);
                setShowImportModal(true);
              }}>
                📥 Import Excel
              </ImportButton>
              <AddButton onClick={() => {
                setFormData({
                  teacherId: '',
                  name: '',
                  email: '',
                  headOfClass: '',
                  teachingSubject: '',
                  classType: [],
                  avatarUrl: '',
                  avatarFile: null
                });
                setShowAddModal(true);
              }}>
                + Tambah Guru
              </AddButton>
            </ActionButtonGroup>
          </ActionHeader>
          
          <TableContainer>
            <Table>
          <thead>
            <tr>
              <th>Foto</th>
              <th>ID Guru</th>
              <th style={{ width: '20%' }}>Nama</th>
              <th style={{ width: '20%' }}>Mata Pelajaran</th>
              <th>Tipe Kelas</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}>Memuat data guru...</td></tr>
            ) : teachers.length === 0 ? (
              <tr><td colSpan={6}>Tidak ada data guru.</td></tr>
            ) : (
              teachers.map(teacher => (
                <tr key={teacher.id}>
                  <td>
                    {teacher.avatarUrl ? (
                      <img 
                        src={teacher.avatarUrl.startsWith('http') ? teacher.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${teacher.avatarUrl}`} 
                        alt={teacher.name} 
                        style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = '/images/user.png';
                        }}
                      />
                    ) : (
                      <img 
                        src="/images/user.png" 
                        alt={teacher.name} 
                        style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    )}
                  </td>
                  <td>{teacher.teacherId}</td>
                  <td>{teacher.name}</td>
                  <td>{teacher.teachingSubject}</td>
                  <td>
                    {Array.isArray(teacher.class_type) && teacher.class_type.length > 0
                      ? teacher.class_type.join(', ')
                      : teacher.class_type || '-'
                    }
                  </td>
                  <td>
                    <Button onClick={() => { setSelectedTeacher(teacher); setShowViewModal(true); }} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Lihat">
                      <ViewIcon />
                    </Button>
                    <Button onClick={() => openEditModal(teacher)} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Edit">
                      <EditIcon />
                    </Button>
                    {teacher.headOfClass && teacher.headOfClass.trim() !== '' && (
                      <Button onClick={() => handleWaliKelasStatus(teacher)} style={{ backgroundColor: '#27ae60', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Wali Kelas Status">
                        <StatusIcon />
                      </Button>
                    )}
                    <DeleteButton onClick={() => openDeleteModal(teacher)} style={{ padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Hapus">
                      <DeleteIcon />
                    </DeleteButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableContainer>
      {/* Pagination Controls */}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 8 }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} aria-label="Sebelumnya">&lt;</button>
        <span>Halaman {currentPage} dari {lastPage}</span>
        <button onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))} disabled={currentPage === lastPage} aria-label="Berikutnya">&gt;</button>
      </div>
        </MainSection>
        <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
      </DashboardGrid>

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>×</CloseNotificationButton>
        </NotificationToast>
      )}

      {/* View Modal */}
      {showViewModal && selectedTeacher && (
        <Modal onClick={() => setShowViewModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Detail Guru</h2>
            <FormGroup>
              <Label>ID Guru</Label>
              <div>{selectedTeacher.teacherId}</div>
            </FormGroup>
            <FormGroup>
              <Label>Nama</Label>
              <div>{selectedTeacher.name}</div>
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <div>{selectedTeacher.email}</div>
            </FormGroup>
            <FormGroup>
              <Label>Wali Kelas Dari</Label>
              <div>{selectedTeacher.headOfClass}</div>
            </FormGroup>
            <FormGroup>
              <Label>Mata Pelajaran</Label>
              <div>{selectedTeacher.teachingSubject}</div>
            </FormGroup>
            <FormGroup>
              <Label>Tipe Kelas</Label>
              <div>
                {Array.isArray(selectedTeacher.class_type) && selectedTeacher.class_type.length > 0
                  ? selectedTeacher.class_type.join(', ')
                  : selectedTeacher.class_type || '-'
                }
              </div>
            </FormGroup>
            <FormGroup>
              <Label>Foto</Label>
              {selectedTeacher.avatarUrl ? (
                <img 
                  src={selectedTeacher.avatarUrl.startsWith('http') ? selectedTeacher.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${selectedTeacher.avatarUrl}`} 
                  alt="Foto Guru" 
                  style={{ maxWidth: 120, borderRadius: 8 }}
                  onError={(e) => {
                    e.target.src = '/images/user.png';
                  }}
                />
              ) : (
                <img 
                  src="/images/user.png" 
                  alt="Foto Guru" 
                  style={{ maxWidth: 120, borderRadius: 8 }}
                />
              )}
            </FormGroup>
            <ButtonGroup>
              <CancelButton type="button" onClick={() => setShowViewModal(false)}>
                Tutup
              </CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Add Teacher Modal */}
      {showAddModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Tambah Guru Baru</h2>
              <CloseButton onClick={() => setShowAddModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleAddTeacher}>
              <FormGroup>
                <Label>ID Guru</Label>
                <Input
                  type="text"
                  name="teacherId"
                  value={formData.teacherId}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Lengkap</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Wali Kelas Dari</Label>
                <Select
                  name="headOfClass"
                  value={formData.headOfClass}
                  onChange={handleInputChange}
                >
                  <option value="">Pilih Wali Kelas</option>
                  {classes.map(className => (
                    <option key={className} value={className}>{className}</option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Mata Pelajaran</Label>
                <Input
                  type="text"
                  name="teachingSubject"
                  value={formData.teachingSubject}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Tipe Kelas</Label>
                <CheckboxGroup>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      id="praktek-add"
                      value="praktek"
                      checked={formData.classType.includes('praktek')}
                      onChange={handleClassTypeChange}
                    />
                    <CheckboxLabel htmlFor="praktek-add">Praktek</CheckboxLabel>
                  </CheckboxItem>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      id="teori-add"
                      value="teori"
                      checked={formData.classType.includes('teori')}
                      onChange={handleClassTypeChange}
                    />
                    <CheckboxLabel htmlFor="teori-add">Teori</CheckboxLabel>
                  </CheckboxItem>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      id="bk-add"
                      value="bk"
                      checked={formData.classType.includes('bk')}
                      onChange={handleClassTypeChange}
                    />
                    <CheckboxLabel htmlFor="bk-add">BK</CheckboxLabel>
                  </CheckboxItem>
                </CheckboxGroup>
              </FormGroup>
              <FormGroup>
                <Label>Foto (avatar)</Label>
                <Input
                  type="file"
                  name="avatar"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files[0];
                    setFormData(prev => ({ ...prev, avatarFile: file }));
                  }}
                />
                {formData.avatarFile ? (
                  <img src={URL.createObjectURL(formData.avatarFile)} alt="Preview" style={{maxWidth: 100, marginTop: 8}} />
                ) : formData.avatarUrl ? (
                  <img src={formData.avatarUrl.startsWith('http') ? formData.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${formData.avatarUrl}`} alt="Preview" style={{maxWidth: 100, marginTop: 8}} />
                ) : null}
              </FormGroup>
              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowAddModal(false)}>Batal</CancelButton>
                <SubmitButton type="submit">Simpan</SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Edit Teacher Modal */}
      {showEditModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Edit Data Guru</h2>
              <CloseButton onClick={() => setShowEditModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleEditTeacher}>
              <FormGroup>
                <Label>ID Guru</Label>
                <Input
                  type="text"
                  name="teacherId"
                  value={formData.teacherId}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Lengkap</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Wali Kelas Dari</Label>
                <Select
                  name="headOfClass"
                  value={formData.headOfClass}
                  onChange={handleInputChange}
                >
                  <option value="">Pilih Wali Kelas</option>
                  {classes.map(className => (
                    <option key={className} value={className}>{className}</option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Mata Pelajaran</Label>
                <Input
                  type="text"
                  name="teachingSubject"
                  value={formData.teachingSubject}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Tipe Kelas</Label>
                <CheckboxGroup>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      id="praktek-edit"
                      value="praktek"
                      checked={formData.classType.includes('praktek')}
                      onChange={handleClassTypeChange}
                    />
                    <CheckboxLabel htmlFor="praktek-edit">Praktek</CheckboxLabel>
                  </CheckboxItem>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      id="teori-edit"
                      value="teori"
                      checked={formData.classType.includes('teori')}
                      onChange={handleClassTypeChange}
                    />
                    <CheckboxLabel htmlFor="teori-edit">Teori</CheckboxLabel>
                  </CheckboxItem>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      id="bk-edit"
                      value="bk"
                      checked={formData.classType.includes('bk')}
                      onChange={handleClassTypeChange}
                    />
                    <CheckboxLabel htmlFor="bk-edit">BK</CheckboxLabel>
                  </CheckboxItem>
                </CheckboxGroup>
              </FormGroup>
              <FormGroup>
                <Label>Foto (avatar)</Label>
                <Input
                  type="file"
                  name="avatar"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files[0];
                    setFormData(prev => ({ ...prev, avatarFile: file }));
                  }}
                />
                {formData.avatarFile ? (
                  <img src={URL.createObjectURL(formData.avatarFile)} alt="Preview" style={{maxWidth: 100, marginTop: 8}} />
                ) : formData.avatarUrl ? (
                  <img src={formData.avatarUrl.startsWith('http') ? formData.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${formData.avatarUrl}`} alt="Preview" style={{maxWidth: 100, marginTop: 8}} />
                ) : null}
              </FormGroup>
              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowEditModal(false)}>Batal</CancelButton>
                <SubmitButton type="submit">Simpan Perubahan</SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal>
          <ModalContent>
            <ModalHeader style={{ background: '#ffeaea', borderRadius: '0.5rem 0.5rem 0 0', borderBottom: '2px solid #e74c3c', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '2rem', color: '#e74c3c', marginRight: 12 }}>⚠️</span>
              <h2 style={{ color: '#e74c3c', margin: 0, fontWeight: 700, fontSize: '1.3rem' }}>Hapus Guru</h2>
              <CloseButton onClick={() => setShowDeleteModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <p style={{ color: '#b71c1c', fontWeight: 500, marginTop: 20 }}>
              Apakah Anda yakin ingin menghapus data guru{' '}
              <strong>{selectedTeacher?.name}</strong>?
            </p>
            <p style={{ color: '#b71c1c', fontSize: '0.95rem', marginBottom: 32 }}>Tindakan ini <strong>TIDAK DAPAT</strong> dibatalkan.</p>
            <ButtonGroup>
              <CancelButton onClick={() => setShowDeleteModal(false)}>
                Batal
              </CancelButton>
              <DeleteButton onClick={handleDeleteTeacher}>
                Hapus
              </DeleteButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Import Data Guru dari Excel</h2>
              <CloseButton onClick={() => setShowImportModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Format Excel yang diperlukan:</Label>
              <ImportFormatInfo>
                <p><strong>Kolom yang WAJIB ada:</strong></p>
                <ul style={{ color: 'red', fontWeight: 'bold' }}>
                  <li><strong>ID Guru</strong> - ID unik guru (tidak boleh kosong)</li>
                  <li><strong>Nama</strong> - Nama lengkap guru (tidak boleh kosong)</li>
                </ul>
                <p><strong>Kolom opsional:</strong></p>
                <ul style={{ color: 'gray' }}>
                  <li><strong>Email</strong> - Email guru (boleh kosong)</li>
                  <li><strong>Wali Kelas</strong> - Kelas yang diampu sebagai wali kelas</li>
                  <li><strong>Mata Pelajaran</strong> - Mata pelajaran yang diajar</li>
                  <li><strong>Tipe Kelas</strong> - Jenis kelas (praktek/teori/bk)</li>
                </ul>
                <p><strong>Catatan:</strong> Jika ID Guru sudah ada di database, data akan diperbarui. Kolom opsional boleh kosong.</p>
              </ImportFormatInfo>
            </FormGroup>

            <FormGroup>
              <Label>Pilih File Excel (.xlsx, .xls)</Label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ccc',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  backgroundColor: '#f5f6fa'
                }}
              />
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'gray' }}>
                Debug: File input should trigger alert when file is selected
              </div>
            </FormGroup>

            {importData.length > 0 && (
              <FormGroup>
                <Label>Preview Data ({importData.length} baris)</Label>
                <PreviewTable>
                  <thead>
                    <tr>
                      <th>Baris</th>
                      <th>ID Guru</th>
                      <th>Nama</th>
                      <th>Email</th>
                      <th>Wali Kelas</th>
                      <th>Mata Pelajaran</th>
                      <th>Tipe Kelas</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((item, index) => (
                      <tr key={index} style={{ backgroundColor: item.valid ? '#f8fff8' : '#fff8f8' }}>
                        <td>{item.row}</td>
                        <td>{item.teacherId}</td>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.headOfClass}</td>
                        <td>{item.teachingSubject}</td>
                        <td>{item.classType.join(', ')}</td>
                        <td>
                          <StatusBadge $valid={item.valid}>
                            {item.valid ? '✓ Valid' : '✗ Error'}
                          </StatusBadge>
                          {!item.valid && item.errors && (
                            <div style={{ fontSize: '0.75rem', color: 'red', marginTop: '0.25rem' }}>
                              {item.errors.join(', ')}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
                {importData.length > 10 && (
                  <p style={{ textAlign: 'center', marginTop: '0.5rem', color: 'var(--text-light)' }}>
                    ... dan {importData.length - 10} baris lainnya
                  </p>
                )}
              </FormGroup>
            )}

            <ButtonGroup>
              <CancelButton onClick={() => setShowImportModal(false)}>
                Batal
              </CancelButton>
              <SubmitButton 
                onClick={handleImportSubmit} 
                disabled={importLoading || importData.length === 0}
              >
                {importLoading ? 'Mengimpor...' : `Import ${importData.filter(item => item.valid).length} Data`}
              </SubmitButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-light);
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TableContainer = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  th {
    background-color: var(--background-light);
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: var(--background-light);
  }
`;

const ActionButton = styled.button`
  background-color: ${props => props.$danger ? 'var(--error)' : 'var(--primary)'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const ActionButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const AddButton = styled.button`
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: #219150;
    opacity: 0.95;
  }
`;

const ImportButton = styled.button`
  background-color: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  margin-right: 1rem;

  &:hover {
    background-color: #2980b9;
    opacity: 0.95;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 1px solid var(--border);
  color: var(--text);
`;

const DeleteButton = styled.button`
  background-color: #e74c3c !important;
  color: #fff !important;
  font-weight: bold;
  border: 2px solid #c0392b;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
  letter-spacing: 0.5px;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s, background-color 0.2s;
  &:hover, &:focus {
    background-color: #c0392b !important;
    outline: 2px solid #e74c3c;
    color: #fff !important;
    opacity: 0.95;
  }
`;


// Notification Toast Styles
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const ImportFormatInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 0.5rem;
  
  p {
    margin: 0 0 0.5rem 0;
  }
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.25rem;
  }
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  
  th, td {
    padding: 0.5rem;
    text-align: left;
    border: 1px solid #dee2e6;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
  
  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.$valid ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$valid ? '#155724' : '#721c24'};
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem 0;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 1.25rem;
  height: 1.25rem;
  accent-color: var(--primary);
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 1rem;
  cursor: pointer;
  user-select: none;
`;

export default ManajemenGuruPage;